/**
 * Custom Incoming Message Subscriber
 * Demonstrates subscriber extensibility for incoming messages
 * Example: Process incoming messages, update UI, trigger notifications, log analytics
 */
import { type IEnvelope } from 'edge-stream-js';
import { type SubscriberMetadata } from '../SubscriberMetadata';

export interface IncomingMessageHandler {
  (envelope: IEnvelope): void | Promise<void>;
}

export class CustomIncomingSubscriber {
  name = 'CustomIncomingSubscriber';

  metadata?: SubscriberMetadata = {
    name: 'CustomIncomingSubscriber',
    description: 'Processes incoming messages for analytics and UI updates. Tracks delivery latency, extracts metrics, triggers notifications for mentions, and logs analytics events',
    version: '1.0.0',
    author: 'BizFirstGO',
    tags: ['analytics', 'incoming', 'notifications', 'ui-update'],
    modifiesData: false,
    impactLevel: 'medium',
    isBlocking: false,
  };

  subscribe(callback: IncomingMessageHandler) {
    return (envelope: IEnvelope) => {
      this.handleIncomingMessage(envelope, callback);
    };
  }

  private async handleIncomingMessage(
    envelope: IEnvelope,
    callback: IncomingMessageHandler,
  ): Promise<void> {
    const messageId = envelope.meta.id;
    const body = envelope.body as any;
    const messageData = body?.data?.data || {};

    console.log(`[CustomIncomingSubscriber] 📨 Received incoming message: ${messageId}`);

    try {
      // Example 1: Extract and process message content
      const { author, text, timestamp, metrics } = messageData;

      console.log(`[CustomIncomingSubscriber] 👤 From: ${author || 'unknown'}`);
      console.log(`[CustomIncomingSubscriber] 💬 Content: ${text || '(empty)'}`);

      if (metrics) {
        console.log(`[CustomIncomingSubscriber] 📊 Metrics:`, {
          words: metrics.wordCount,
          characters: metrics.characterCount,
          urls: metrics.hasUrls,
          mentions: metrics.hasMentions,
        });
      }

      // Example 2: Track message delivery time
      const receivedTime = new Date(timestamp);
      const deliveredTime = new Date();
      const latency = deliveredTime.getTime() - receivedTime.getTime();

      console.log(`[CustomIncomingSubscriber] ⏱️ Latency: ${latency}ms`);

      // Example 3: Trigger notifications (simulated)
      if (text && text.includes('@')) {
        console.log(`[CustomIncomingSubscriber] 🔔 Mentioned in message`);
      }

      // Example 4: Update UI (would be actual UI update in real app)
      console.log(`[CustomIncomingSubscriber] 🎨 Would update UI with message`);

      // Example 5: Store for analytics/history
      const analyticsEvent = {
        messageId,
        author,
        timestamp,
        latency,
        contentLength: text?.length,
        deliveredAt: deliveredTime.toISOString(),
      };

      console.log(`[CustomIncomingSubscriber] 📈 Analytics event:`, analyticsEvent);

      // Call the original callback if provided
      if (callback) {
        await callback(envelope);
      }

      console.log(`[CustomIncomingSubscriber] ✅ Incoming message processed: ${messageId}`);
    } catch (error) {
      console.error(`[CustomIncomingSubscriber] ❌ Error processing message: ${messageId}`, error);
      throw error;
    }
  }
}
